import { useState } from "react";
import { useParams } from "react-router-dom";

import { FileUpload } from "@/model/Resource/ResourceRequest";
import { ErrorToast } from "@/shared/notifications/AlertToast";

import { insertDomain } from "@/service/ApplicationQueries";
import { UploadChecklistAttachment } from "@/service/ResourceMutations";
import { RouteParams } from "@/service/Utilities";

export type UploadImageRequest = (
	file: File,
) => Promise<string | FileUpload | undefined>;

interface UseUploadImageOptions {
	uploadImageRequest?: UploadImageRequest;
}

interface UseUploadImageResult {
	uploadImage: (file: File) => Promise<string | undefined>;
	isUploading: boolean;
}

const useUploadImage = ({
	uploadImageRequest,
}: UseUploadImageOptions = {}): UseUploadImageResult => {
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const { id: procedureId } = useParams<RouteParams>();

	const uploadAttachment = UploadChecklistAttachment();

	const uploadImage = async (file: File) => {
		try {
			setIsUploading(true);

			if (!file) {
				throw new Error("No file selected");
			}

			const response = uploadImageRequest
				? await uploadImageRequest(file)
				: await (() => {
						if (!procedureId) {
							throw new Error("Procedure ID is required for image upload");
						}

						return uploadAttachment({
							File: file,
							procedureId: parseInt(procedureId, 10),
						});
					})();

			const urlPath =
				typeof response === "string" ? response : response?.UrlPath;

			if (urlPath) {
				return insertDomain(urlPath, {
					insertIntoHtmlElement: false,
				});
			}

			throw new Error("Image upload did not return a file path");
		} catch (err: unknown) {
			console.error("Error uploading image:", err);
			ErrorToast("Failed to upload image");
		} finally {
			setIsUploading(false);
		}
	};

	return { uploadImage, isUploading };
};

export default useUploadImage;
